import React, { useEffect, useState, useCallback } from 'react';
import { FaTimes } from 'react-icons/fa';

export type DrawerPosition = 'left' | 'right';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl';

interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  position?: DrawerPosition;
  size?: DrawerSize;
  showOverlay?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  headerClassName?: string;
  bodyClassName?: string;
}

const sizeClasses: Record<DrawerSize, string> = {
  sm: 'w-72 sm:w-80',
  md: 'w-80 sm:w-96',
  lg: 'w-96 sm:w-[28rem]',
  xl: 'w-[22rem] sm:w-[32rem]',
};

/**
 * Reusable Side Drawer component
 * Slides in from left or right side of the screen
 * Supports overlay, escape key, and click-outside to close
 */
export const SideDrawer: React.FC<SideDrawerProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  position = 'right',
  size = 'md',
  showOverlay = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  headerClassName = '',
  bodyClassName = '',
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle open/close animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle Escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && closeOnEscape && isOpen) {
      onClose();
    }
  }, [closeOnEscape, isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  // Handle overlay click
  const handleOverlayClick = useCallback(() => {
    if (closeOnOverlayClick) {
      onClose();
    }
  }, [closeOnOverlayClick, onClose]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  const positionClasses = position === 'right'
    ? `right-0 ${isAnimating ? 'translate-x-0' : 'translate-x-full'}`
    : `left-0 ${isAnimating ? 'translate-x-0' : '-translate-x-full'}`;

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      {showOverlay && (
        <div
          className={`absolute inset-0 bg-black/40 backdrop-blur-[6px] transition-opacity duration-300 ${
            isAnimating ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={handleOverlayClick}
        />
      )}

      {/* Drawer */}
      <div
        className={`
          absolute top-0 h-full ${sizeClasses[size]} ${positionClasses}
          bg-card shadow-2xl ring-1 ring-border/60 flex flex-col
          transform transition-transform duration-300 ease-out
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || subtitle) && (
          <div className={`flex items-center justify-between px-5 py-4 bg-gradient-to-r from-muted to-muted border-b border-border/80 ${headerClassName}`}>
            <div className="min-w-0 flex-1">
              {title && (
                <h2 className="text-base font-semibold text-foreground tracking-tight truncate">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {subtitle}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="ml-3 w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 flex-shrink-0"
              aria-label="Close"
            >
              <FaTimes className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Body */}
        <div className={`flex-1 overflow-y-auto ${bodyClassName}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default SideDrawer;
